import axios from "axios";
import format from "date-fns/format";
import getDay from "date-fns/getDay";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import React, { useEffect, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Alert, Badge, Button, Form, Modal, Table } from "react-bootstrap";
import { FaCalendarAlt } from "react-icons/fa";
import { getURLbyEndPointV2 } from "../../../../../store/api";
import { getAuthUserlocal } from "../../../../../store/services";

const locales = {
    "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

const timeSlots = [];
for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
        const startTime = new Date(0, 0, 0, hour, minute).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });
        const endTime = new Date(0, 0, 0, hour, minute + 30).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });
        timeSlots.push(`${startTime} - ${endTime}`);
    }
}

const minDate = new Date(2026, 0, 15);
const maxDate = new Date(2026, 2, 30);

// Utility function to parse time strings like "11:30 AM" into hours and minutes
const parseTime = (timeStr) => {
    const [time, period] = timeStr.split(" ");
    let [hours, minutes] = time.split(":").map(Number);
    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
    return [hours, minutes];
};

const EditScheduleCalender = ({ formData, setFormData, existingSlots, isEditMode, onSlotsUpdate }) => {
    const [events, setEvents] = useState([]);
    const [pendingSlots, setPendingSlots] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState("");
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (existingSlots && existingSlots.length > 0) {
            const formattedEvents = existingSlots.map((slot) => {
                const start = new Date(slot.date);
                const end = new Date(slot.date);

                const [startHours, startMinutes] = parseTime(slot.startTime);
                const [endHours, endMinutes] = parseTime(slot.endTime);

                start.setHours(startHours, startMinutes, 0, 0);
                end.setHours(endHours, endMinutes, 0, 0);

                return {
                    id: slot._id,
                    title: `${slot.startTime} - ${slot.endTime} ${slot.isBooked === "Booked" ? "(Booked)" : ""}`,
                    start,
                    end,
                    resource: slot,
                };
            });
            setEvents(formattedEvents);
        }
    }, [existingSlots]);

    const isDateDisabled = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time part to compare dates only
        
        const compareDate = new Date(date);
        compareDate.setHours(0, 0, 0, 0); // Reset time part to compare dates only
        
        return compareDate < today || compareDate < minDate || compareDate > maxDate || date.getDay() === 0 || date.getDay() === 6;
    };

    const dayPropGetter = (date) => {
        if (isDateDisabled(date)) {
            return {
                className: "disabled-day",
                style: { backgroundColor: "#f8d7da", cursor: "not-allowed" },
            };
        }
        return {};
    };

    const validateTimeSlots = (newDate) => {
        const allSlots = [...formData.selectedTimeSlots, ...pendingSlots];
        const slotsByMonth = {};
        const slotsByDay = {};

        for (const slot of allSlots) {
            const date = new Date(slot.date);
            const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
            const dayKey = date.toDateString();

            slotsByMonth[monthKey] = slotsByMonth[monthKey] || [];
            slotsByDay[dayKey] = slotsByDay[dayKey] || [];

            slotsByMonth[monthKey].push(slot);
            slotsByDay[dayKey].push(slot);
        }

        const monthKey = `${newDate.getFullYear()}-${newDate.getMonth()}`;
        const dayKey = newDate.toDateString();

        // Check maximum 2 slots per day
        if (slotsByDay[dayKey] && slotsByDay[dayKey].length >= 2) {
            return "Maximum 2 slots per day allowed";
        }

        // Check maximum 8 slots per month
        if (slotsByMonth[monthKey] && slotsByMonth[monthKey].length >= 8) {
            return "Maximum 8 slots per month reached. You cannot add more slots this month.";
        }

        return null;
    };

    const handleSelectSlot = (slotInfo) => {
        const selectedDate = new Date(slotInfo.start);
        if (!isDateDisabled(selectedDate)) {
            const validationError = validateTimeSlots(selectedDate);
            if (validationError) {
                setError(validationError);
                return;
            }
            setSelectedDate(selectedDate);
            setSelectedTime("");
            setSelectedEvent(null);
            setShowModal(true);
        }
    };

    const handleSelectEvent = (event) => {
        if (event.resource.isBooked === "Booked") {
            setError("Booked slots cannot be modified");
            return;
        }
        setSelectedEvent(event);
        setSelectedDate(new Date(event.start));
        setSelectedTime(`${event.resource.startTime} - ${event.resource.endTime}`);
        setShowModal(true);
    };

    const handleAddOrUpdateSlot = () => {
        if (!selectedTime || !selectedDate) {
            setError("Please select a date and time slot.");
            return;
        }

        const [startTime, endTime] = selectedTime.split(" - ");
        const [startHours, startMinutes] = parseTime(startTime);
        const dateWithTime = new Date(selectedDate);
        dateWithTime.setHours(startHours, startMinutes, 0, 0); 

        const newSlot = {
            _id: selectedEvent?.id,
            date: dateWithTime.toISOString(),
            startTime,
            endTime,
            duration: "30",
        };

        const slotExists = events.some(
            (event) =>
                event.id !== selectedEvent?.id &&
                new Date(event.resource.date).toDateString() === selectedDate.toDateString() &&
                event.resource.startTime === startTime
        );

        if (slotExists) {
            setError("This time slot is already scheduled for the selected date.");
            return;
        }

        const [endHours, endMinutes] = parseTime(endTime);
        const start = new Date(selectedDate);
        const end = new Date(selectedDate);
        start.setHours(startHours, startMinutes, 0, 0);
        end.setHours(endHours, endMinutes, 0, 0);

        if (selectedEvent) {
            // This is an update to an existing slot
            const updatedSlot = {
                _id: selectedEvent.id,
                date: newSlot.date,
                startTime: newSlot.startTime,
                endTime: newSlot.endTime,
                duration: newSlot.duration,
            };
            
            // Call the parent component's onSlotsUpdate function with the updated slot
            onSlotsUpdate([updatedSlot]);
            
            // Update the local state
            setEvents((prevEvents) =>
                prevEvents.map((event) =>
                    event.id === selectedEvent.id
                        ? {
                            ...event,
                            title: `${startTime} - ${endTime}`,
                            start,
                            end,
                            resource: { ...event.resource, ...newSlot },
                        }
                        : event
                )
            );
        } else {
            // This is a new slot
            const tempId = `temp-${Date.now()}`;
            const pendingSlot = {
                id: tempId,
                date: newSlot.date,
                startTime,
                endTime,
                duration: "30",
            };
            
            // Add to pending slots
            setPendingSlots((prev) => [...prev, pendingSlot]);
            
            // Update the local state
            setEvents((prev) => [
                ...prev,
                {
                    id: tempId,
                    title: `${startTime} - ${endTime}`,
                    start,
                    end,
                    resource: pendingSlot,
                },
            ]);
        }

        setShowModal(false);
        setError("");
    };

    const handleSaveAllSlots = async () => {
        const userId = getAuthUserlocal()._id;
        if (pendingSlots.length === 0) {
            setError("No new slots to save.");
            return;
        }

        try {
            setLoading(true);
            
            // Call the parent component's onSlotsUpdate function with all pending slots
            // This will handle the API call and state update
            onSlotsUpdate(pendingSlots);
            
            // Clear pending slots after they've been sent to the parent
            setPendingSlots([]);
            
        } catch (err) {
            setError(err.response?.data?.message || "Error saving slots");
        } finally {
            setLoading(false);
        }
    };

    const eventPropGetter = (event) => {
        const isBooked = event.resource.isBooked === "Booked";
        return {
            style: {
                backgroundColor: isBooked ? "#28a745" : "#007bff",
                borderColor: isBooked ? "#1e7e34" : "#0056b3",
                opacity: 0.8,
                color: "white",
                cursor: isBooked ? "not-allowed" : "pointer",
            },
        };
    };

    return (
        <div className="mb-4">
            {error && <Alert variant="danger">{error}</Alert>}

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                    <FaCalendarAlt className="me-2" />
                    Manage Your Availability Slots
                </h6>
                {pendingSlots.length > 0 && (
                    <Button variant="success" onClick={handleSaveAllSlots} disabled={loading}>
                        {loading ? "Saving..." : `Save ${pendingSlots.length} Slot${pendingSlots.length > 1 ? "s" : ""}`}
                    </Button>
                )}
            </div>

            <div className="calendar-container" style={{ height: 500 }}>
                <Calendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    selectable={isEditMode}
                    onSelectSlot={handleSelectSlot}
                    onSelectEvent={handleSelectEvent}
                    eventPropGetter={eventPropGetter}
                    views={["month", "week", "day"]}
                    defaultView="month"
                    min={minDate}
                    max={maxDate}
                    dayPropGetter={dayPropGetter}
                    defaultDate={minDate}
                />
            </div>

            <div className="mt-4">
                <h6>Scheduled Slots</h6>
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Time</th>
                            <th>Duration</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {events.length > 0 ? (
                            events.map((event) => (
                                <tr
                                    key={event.id}
                                    className={event.resource.isBooked === "Booked" ? "table-success" : ""}
                                    style={{ cursor: event.resource.isBooked === "Booked" ? "not-allowed" : "pointer" }}
                                    onClick={() => event.resource.isBooked !== "Booked" && handleSelectEvent(event)}
                                >
                                    <td>{format(new Date(event.resource.date), "MMMM d, yyyy")}</td>
                                    <td>{`${event.resource.startTime} - ${event.resource.endTime}`}</td>
                                    <td>{event.resource.duration} min</td>
                                    <td>
                                        {event.resource.isBooked === "Booked" ? (
                                            <Badge bg="success">Booked</Badge>
                                        ) : (
                                            <Badge bg="warning" text="dark">Pending</Badge>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="text-center">
                                    No slots scheduled
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedEvent ? "Edit Time Slot" : "Add New Time Slot"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Date Selected: {selectedDate ? format(selectedDate, "MMMM d, yyyy") : ""}</p>
                    <Form.Group>
                        <Form.Label>Select Time</Form.Label>
                        <Form.Select value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)}>
                            <option value="">Choose a time slot</option>
                            {timeSlots.map((time, index) => (
                                <option key={index} value={time}>
                                    {time}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleAddOrUpdateSlot} disabled={loading}>
                        {loading ? "Saving..." : selectedEvent ? "Update Time Slot" : "Add Time Slot"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default EditScheduleCalender;